// This sidebar is mobile/tablet bottom menu
import React, { useEffect, useState } from "react";
import { Button, Form, Image, Modal, Nav, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import config from "../../config";
import { useMenu } from "../../menu-items";
import { isNullOrUndefined } from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { changeLanguage, changeMode } from "../../actions/store";
import { modeTypes } from "../../constants/mode";
import HybridGameLogTab from "../hybridGameLog/HybridGameLogTab";
import { languages } from "../../constants/language";
import Swal from "sweetalert2";
import { seamlessGetDepositWithdrawHistory } from "../../services/seamless.service";
import { hybridGetDepositWithdrawHistory } from "../../services/hybridPlayer.service";
import { mapHybridDepositWithdrawHistoryResponse } from "../../utils/hybridDepositWithdrawHistory";
import { useFeatureRelease } from "../../hooks/useFeatureRelease";

// Define types for menu items
interface MenuItem {
	id: string;
	label: string;
	value: string | (() => void); // Type can be string or function
	type: "link" | "action" | string;
	mobile?: boolean;
	icon?: any;
	iconType?: string;
}

const MobileBottomNav: React.FC = () => {
	const currentMode = localStorage.getItem("currentMode") || modeTypes.TRANSFER;
	const { t } = useTranslation();
	const menu = useMenu();
	const dispatch = useDispatch();
	const location = useLocation(); // Get current location
	const defaultActiveItem =
		menu.find((item) => item.type === "link" && typeof item.value === "string")
			?.id || null;
	const [activeItem, setActiveItem] = useState<string | null>(
		defaultActiveItem
	); // Track active item
	const { userProfile } = useSelector((state: any) => state.userProfile);
	const [showModeModal, setShowModeModal] = useState(false);
	const [showLanguageModal, setShowLanguageModal] = useState(false);
	const imageUrl = `${config.awsUrl}${config.region}/Landing/`;
	const [balance, setBalance] = useState(0);
	const { enabled: chatEnabled } = useFeatureRelease(
		"USER_LOBBY_B2C_SELF_REGISTER"
	);
	// Determine if an item is active
	const isActive = (item: MenuItem) => {
		if (item.type === "link") {
			return item.value === location.pathname || activeItem === item.id;
		}
		return activeItem === item.id;
	};

	const getBottomNavLabel = (item: MenuItem) => {
		if (currentMode === modeTypes.HYBRID && item.id === "referral_code") {
			return t("Referral");
		}
		return t(item.label);
	};

	const renderBottomNavIcon = (item: MenuItem, iconSrc: string) => (
		<div className="bottom-nav-icon-wrap">
			{item.iconType === "fontAwesome" ? (
				<FontAwesomeIcon
					icon={item.icon}
					className="bottom-nav-icon-svg"
					fixedWidth
				/>
			) : (
				<Image src={iconSrc} alt={item.id} className="bottom-nav-icon-img" />
			)}
		</div>
	);

	const handleLanguageSelect = (lang: string) => {
		dispatch(changeLanguage(lang));
		localStorage.setItem("Language", lang);
		setShowLanguageModal(false);
	};

	const handleClick = (itemId: string, itemValue: string | (() => void)) => {
		if (typeof itemValue === "function") {
			itemValue();
		}
		setActiveItem(itemId); // Set active item on click
	};

	const handleModeSelect = (mode: string) => {
		dispatch(changeMode(mode));
		localStorage.setItem("currentMode", mode);
		setShowModeModal(false);
	};

	const [showAccountModal, setShowAccountModal] = useState(false);
	const [showHistoryModal, setShowHistoryModal] = useState(false);
	// Hybrid only: History modal tabs (deposit/withdraw vs game logs)
	const [historyTab, setHistoryTab] = useState<"transactions" | "gamelogs">(
		"transactions"
	);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");

	const [activeTab, setActiveTab] = useState("cash");

	const handleTabClick = (tab) => setActiveTab(tab);

	const [cashData, setCashData] = useState([]);

	// cashData to call api when showHistoryModal open
	useEffect(() => {
		if (!showHistoryModal) return;

		const historyRequest =
			currentMode == modeTypes.HYBRID
				? hybridGetDepositWithdrawHistory()
				: seamlessGetDepositWithdrawHistory();

		historyRequest
			.then((result: any) => {
				if (!result?.status) {
					setCashData([]);
					return;
				}
				if (currentMode == modeTypes.HYBRID) {
					setCashData(mapHybridDepositWithdrawHistoryResponse(result.data));
					return;
				}
				setCashData(Array.isArray(result.data) ? result.data : []);
			})
			.catch(() => {
				setCashData([]);
			});
	}, [showHistoryModal, currentMode]);

	const pick = (ids: string[]) =>
		ids
			.map((id) => menu.find((item) => item.id === id))
			.filter((item): item is NonNullable<typeof item> => Boolean(item));

	const isLoggedIn = !isNullOrUndefined(userProfile);
	let filteredMenu = menu;

	if (currentMode === modeTypes.SEAMLESS) {
		filteredMenu = isLoggedIn
			? pick(["home", "history", "language", "account"])
			: pick(["home", "promotion", "download", "language"]);
	}

	if (currentMode === modeTypes.HYBRID) {
		filteredMenu = isLoggedIn
			? pick([
					"home",
					"account",
					"deposit",
					"withdraw",
					"referral_code",
					"chat",
				])
			: pick(["home", "promotion", "download", "language"]);
	}

	if (currentMode === modeTypes.TRANSFER) {
		filteredMenu = pick(["home", "promotion", "download", "language"]);
	}

	// Bottom nav: chat only for logged-in hybrid, regardless of chatEnabled
	if (!(currentMode === modeTypes.HYBRID && isLoggedIn)) {
		filteredMenu = filteredMenu.filter((item) => item.id !== "chat");
	}

	return (
		<nav
			id="mobileNav"
			className={`navbar fixed-bottom navbar-light p-0 ${currentMode === modeTypes.SEAMLESS ? "seamless-mode" : ""}`}
			role="navigation"
		>
			<Nav className="subnav">
				<div className="d-flex flex-row justify-content-evenly w-100">
					{filteredMenu.map((item) => {
						if (!item.mobile) return null;

						const iconSrc = `/icons/${item.id}.png`;
						const activeClass = isActive(item) ? "active" : "";

						return (
							<div
								key={item.id}
								className={`nav-item`}
								onClick={() => handleClick(item.id, item.value)}
							>
								{item.type === "link" && typeof item.value === "string" ? (
									// Type assertion on if declaration to ensure 'value' is a string
									<Link
										to={item.value} // do not do assertion here, as it will break the build package
										className={`nav-link ${activeClass}`}
										onClick={() => setActiveItem(item.id)} // Set active item when link is clicked
									>
										{renderBottomNavIcon(item, iconSrc)}
										<div className="bottom-nav-label">
											{getBottomNavLabel(item)}
										</div>
									</Link>
								) : item.type === "action" &&
								  typeof item.value === "function" ? (
									<div className={`logout-nav nav-link ${activeClass}`}>
										{renderBottomNavIcon(item, iconSrc)}
										<div className="bottom-nav-label">
											{getBottomNavLabel(item)}
										</div>
									</div>
								) : item.type === "dropdown" ? (
									<div
										className={`nav-link`}
										onClick={() => setShowLanguageModal(true)}
									>
										<Image
											src={imageUrl + "icons/" + item.id + ".png"}
											alt={item.id}
										/>
										{getBottomNavLabel(item)}
									</div>
								) : item.type === "modalhistory" ? (
									<div
										className={`nav-link`}
										onClick={() => setShowHistoryModal(true)}
									>
										{renderBottomNavIcon(item, iconSrc)}
										<div className="bottom-nav-label">
											{getBottomNavLabel(item)}
										</div>
									</div>
								) : item.type === "modalacc" ? (
									<div
										className={`nav-link`}
										onClick={() => setShowAccountModal(true)}
									>
										{item.icon ? (
											<FontAwesomeIcon
												icon={item.icon}
												style={{ marginBottom: "4px" }}
											/>
										) : (
											<Image
												src={imageUrl + "icons/" + item.id + ".png"}
												alt={item.id}
												style={{ marginBottom: "4px" }}
											/>
										)}
										{t(item.label)}
									</div>
								) : item.type === "mode" && isNullOrUndefined(userProfile) ? (
									<div
										className={`logout-nav`}
										onClick={() => setShowModeModal(true)}
									>
										<FontAwesomeIcon icon={item.icon} className="me-3" />
										{t(item.label)}
									</div>
								) : null}
							</div>
						);
					})}
				</div>
			</Nav>

			<Modal
				show={showLanguageModal}
				onHide={() => setShowLanguageModal(false)}
				className={
					currentMode === modeTypes.SEAMLESS
						? "modal-language seamless-mode"
						: "modal-language transfer-mode"
				}
				centered
				size="sm"
			>
				<button
					className="floating-close-button"
					onClick={() => setShowLanguageModal(false)}
				>
					<FontAwesomeIcon icon={faTimes} />
				</button>
				<Modal.Body className="d-flex flex-column align-items-center">
					<h5 className="modal-lang-title">{t("Select Language")}</h5>
					{languages.map((lang, index) => (
						<React.Fragment key={index}>
							<div
								onClick={() => handleLanguageSelect(lang.value)}
								style={{
									display: "flex",
									alignItems: "center",
									width: "150px",
									padding: "10px 24px",
									cursor: "pointer",
									textAlign: "center",
								}}
							>
								<span
									role="img"
									aria-label={lang.label || "en-US"}
									style={{ marginRight: 8, fontSize: 25 }}
								>
									{lang.icon || "🌐"}
								</span>
								<span style={{ fontSize: 16 }}>{lang.label}</span>
							</div>
							{index < languages.length - 1 && (
								<div className="horizontal-line" />
							)}
						</React.Fragment>
					))}
				</Modal.Body>
			</Modal>

			<Modal
				show={showHistoryModal}
				onHide={() => setShowHistoryModal(false)}
				className={
					currentMode === modeTypes.SEAMLESS
						? "modal-history seamless-mode"
						: "modal-history transfer-mode"
				}
				centered
				size="lg"
			>
				<button
					className="floating-close-button"
					onClick={() => setShowHistoryModal(false)}
				>
					<FontAwesomeIcon icon={faTimes} />
				</button>
				<Modal.Body className="flex-column align-items-center">
					<h5 className="modal-history-title">{t("History")}</h5>

					{currentMode === modeTypes.HYBRID && (
						<div className="history-tabs" role="tablist">
							<button
								type="button"
								role="tab"
								aria-selected={historyTab === "transactions"}
								className={historyTab === "transactions" ? "active" : ""}
								onClick={() => setHistoryTab("transactions")}
							>
								{t("Transactions")}
							</button>
							<button
								type="button"
								role="tab"
								aria-selected={historyTab === "gamelogs"}
								className={historyTab === "gamelogs" ? "active" : ""}
								onClick={() => setHistoryTab("gamelogs")}
							>
								{t("Game Logs")}
							</button>
						</div>
					)}

					{currentMode === modeTypes.HYBRID && historyTab === "gamelogs" ? (
						<HybridGameLogTab active={showHistoryModal} />
					) : (
						<div className="history-table-container">
							<Table striped bordered hover size="sm" responsive>
								<thead>
									<tr>
										<th>{t("DateTime")}</th>
										<th>{t("Type")}</th>
										<th>{t("Amount")}</th>
										<th>{t("Remark")}</th>
										<th>{t("Status")}</th>
									</tr>
								</thead>
								<tbody>
									{cashData.map((item, index) => (
										<tr key={index}>
											<td>{item.datetime}</td>
											<td>{item.type}</td>
											<td>{item.amount}</td>
											<td>{item.remark}</td>
											<td>{item.status}</td>
										</tr>
									))}
								</tbody>
							</Table>
						</div>
					)}
				</Modal.Body>
			</Modal>
		</nav>
	);
};

export default MobileBottomNav;
